/**
 * APK güncelleme manifesti — saf doğrulama ve URL çözümü.
 *
 * Manifest sunucudan gelir; istemci yalnızca sayısal `versionCode` ile karşılaştırır.
 * APK otomatik kurulmaz; indirme sayfası kullanıcı onayıyla açılır.
 */
export interface ReleaseManifest {
  versionCode: number;
  versionName: string;
  url: string;
  sha256: string;
  minSdk?: number;
  publishedAt?: number;
  mandatory?: boolean;
  notes?: string;
}

export function parseReleaseManifest(value: unknown): ReleaseManifest | null {
  if (typeof value !== 'object' || value === null) return null;
  const input = value as Record<string, unknown>;
  if (!Number.isInteger(input.versionCode) || (input.versionCode as number) < 1) return null;
  if (typeof input.versionName !== 'string' || input.versionName.trim().length === 0) return null;
  if (typeof input.url !== 'string' || input.url.trim().length === 0) return null;
  if (typeof input.sha256 !== 'string' || !/^[a-f0-9]{64}$/i.test(input.sha256)) return null;

  return {
    versionCode: input.versionCode as number,
    versionName: input.versionName,
    url: input.url,
    sha256: input.sha256.toLowerCase(),
    ...(typeof input.minSdk === 'number' ? { minSdk: input.minSdk } : {}),
    ...(typeof input.publishedAt === 'number' ? { publishedAt: input.publishedAt } : {}),
    ...(typeof input.mandatory === 'boolean' ? { mandatory: input.mandatory } : {}),
    ...(typeof input.notes === 'string' ? { notes: input.notes } : {}),
  };
}

/** Yeni APK gerçekten daha yüksek versionCode taşıyor mu? */
export function isNewerVersion(currentVersionCode: number, latest: ReleaseManifest): boolean {
  return Number.isInteger(currentVersionCode) && latest.versionCode > currentVersionCode;
}

/**
 * Güncelleme bandı gösterilmeli mi?
 *
 * `currentBuild` Android'in `versionCode` değeridir (string gelebilir).
 * Kullanıcı aynı sürümü bu oturumda kapattıysa tekrar gösterilmez.
 */
export function shouldOfferUpdate(
  currentBuild: string | number,
  latest: ReleaseManifest | null,
  dismissedVersionCode?: string | null,
): boolean {
  if (latest === null) return false;
  const code = typeof currentBuild === 'number' ? currentBuild : Number.parseInt(currentBuild, 10);
  if (!Number.isInteger(code) || !isNewerVersion(code, latest)) return false;
  return dismissedVersionCode !== String(latest.versionCode);
}

/**
 * İndirme URL'sini yalnızca uygulamanın kendi origin'inde kabul eder.
 * Manifest aynı origin'den gelse bile `javascript:` veya başka host'a izin verilmez.
 */
export function resolveReleaseUrl(url: string, origin: string): string | null {
  try {
    const parsed = new URL(url, origin);
    if (parsed.origin !== origin) return null;
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}
